/**
 * CR-5551: Supplier-side modal to raise a Concession or request a Production
 * Permit directly against a PO line.
 *
 * Spec ask: "Suppliers naturally identify issues against an order, a delivery,
 * or a shipment — not from a standalone quality menu." This modal is the
 * PO-line-driven entry point. The Customer / PO Number / Line / Part Number /
 * Description / Quantity Ordered fields auto-populate from the line so the
 * supplier only fills the deviation-specific bits.
 *
 * The PE/CE distinction is fixed by which button launched the modal — we
 * surface the headline "Production Permit (PE)" or "Concession (CE)" in the
 * dialog title instead of asking the user to pick again.
 */
import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { qualityAPI, PurchaseOrderLine } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import type { ConcessionDeviationType, ConcessionRequestType, ConcessionRiskLevel } from '../../lib/api/types/quality';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import FileDropZone from '../common/FileDropZone';

interface RaiseConcessionFromLineModalProps {
  isOpen: boolean;
  onClose: () => void;
  line: PurchaseOrderLine;
  requestType: ConcessionRequestType;
  onSuccess?: () => void;
}

export function RaiseConcessionFromLineModal({ isOpen, onClose, line, requestType, onSuccess }: RaiseConcessionFromLineModalProps) {
  const { formatQuantity } = useFormatters();
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [proposedDisposition, setProposedDisposition] = useState('');
  const [deviationType, setDeviationType] = useState<ConcessionDeviationType>('dimensional');
  const [riskLevel, setRiskLevel] = useState<ConcessionRiskLevel>('medium');
  const [riskJustification, setRiskJustification] = useState('');
  // CR-6510: the legacy deviation form's own identifiers. Optional, because a
  // supplier often raises the request before the investigation has finished.
  const [supplierRef, setSupplierRef] = useState('');
  const [mmAndRev, setMmAndRev] = useState('');
  const [serialNumbers, setSerialNumbers] = useState('');
  const [previousDeviationRef, setPreviousDeviationRef] = useState('');
  // CR-6510: evidence at the point of raising. Suppliers often have their own
  // concession template, or the deviation is visual and only a photo explains
  // it — making them save first and reopen the record to attach it is how the
  // evidence ends up never being attached at all.
  const [files, setFiles] = useState<File[]>([]);
  const [customerRequirement, setCustomerRequirement] = useState('');
  const [rootCauseCorrectiveAction, setRootCauseCorrectiveAction] = useState('');
  // Auto-populate quantity from the line — the supplier almost always
  // wants to deviate the whole ordered qty; they can edit if not.
  // CR-7262: seed the real quantity, not a rounded one. This value is
  // submitted, so it stays a plain parseable number -- deliberately NOT
  // formatQuantity, whose thousands separators would not survive parseFloat.
  // String(parseFloat) drops the column's trailing zeros on its own: the
  // API's "56.440" seeds "56.44", and "560.000" seeds "560".
  const [quantityAffected, setQuantityAffected] = useState<string>(
    line.quantity ? String(parseFloat(line.quantity)) : ''
  );

  const headline = requestType === 'production_permit' ? 'Request Production Permit (PE)' : 'Raise Concession (CE)';
  const isValid = title.trim() && description.trim() && reason.trim() && quantityAffected;

  const handleSubmit = async () => {
    if (!isValid) return;
    if (!line.customer_id || !line.supplier_id) {
      toast.error('PO line is missing customer/supplier — refresh and retry.');
      return;
    }
    try {
      setSubmitting(true);
      // CR-5551: customer + supplier come from the PO line itself. The
      // serializer also expects po_line, po_number, part_number etc. so
      // suppliers don't have to retype anything that the order already
      // tells us.
      const created = await qualityAPI.createConcession({
        customer: line.customer_id,
        supplier: line.supplier_id,
        po_line: line.id,
        po_number: line.po_number,
        part_number: line.part_number,
        part_description: line.description || '',
        quantity_affected: parseFloat(quantityAffected),
        deviation_type: deviationType,
        request_type: requestType,
        title: title.trim(),
        description: description.trim(),
        reason: reason.trim(),
        proposed_disposition: proposedDisposition.trim(),
        risk_level: riskLevel,
        risk_justification: riskJustification.trim(),
        supplier_ref: supplierRef.trim(),
        mm_and_rev: mmAndRev.trim(),
        serial_numbers: serialNumbers.trim(),
        previous_deviation_ref: previousDeviationRef.trim(),
        customer_requirement: customerRequirement.trim(),
        root_cause_corrective_action: rootCauseCorrectiveAction.trim(),
        status: 'submitted',
      });

      // Uploaded after the concession exists, because each document is a row
      // against it. A file that fails to attach must not discard the request
      // the supplier just wrote, so this reports and carries on rather than
      // throwing into the catch below.
      const concessionId = created?.data?.id;
      if (concessionId && files.length) {
        const failed: string[] = [];
        for (const file of files) {
          try {
            const form = new FormData();
            form.append('concession', String(concessionId));
            form.append('file', file);
            form.append('file_name', file.name);
            await qualityAPI.uploadConcessionDocument(form);
          } catch {
            failed.push(file.name);
          }
        }
        if (failed.length) {
          toast.error(
            `Request submitted, but these did not attach: ${failed.join(', ')}. `
            + 'You can add them from the concession record.',
          );
        }
      }

      toast.success(`${requestType === 'production_permit' ? 'Production Permit request' : 'Concession'} submitted`);
      setFiles([]);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{headline}</DialogTitle>
          <DialogDescription>
            Submit a deviation request linked to PO {line.po_number} / Line {line.line_number} for customer review.
          </DialogDescription>
        </DialogHeader>

        {/* Auto-populated context (read-only) */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-md text-sm">
          <div>
            <span className="text-muted-foreground">Customer:</span>{' '}
            <span className="font-medium">{line.customer_name || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Supplier:</span>{' '}
            <span className="font-medium">{line.supplier_name || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">PO / Line:</span>{' '}
            <span className="font-medium">{line.po_number} / {line.line_number}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Qty Ordered:</span>{' '}
            <span className="font-medium">{formatQuantity(line.quantity)}</span>
          </div>
          <div className="col-span-2">
            <span className="text-muted-foreground">Part:</span>{' '}
            <span className="font-medium">{line.part_number}</span>
            {line.part_description && <span className="text-muted-foreground"> — {line.part_description}</span>}
          </div>
        </div>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="cr5551-title">Title *</Label>
            <Input
              id="cr5551-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief title of the deviation"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cr5551-qty">Affected Quantity *</Label>
              <Input
                id="cr5551-qty"
                type="number"
                step="any"
                min="0"
                value={quantityAffected}
                onChange={(e) => setQuantityAffected(e.target.value)}
                placeholder="Number of units"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cr5551-devtype">Deviation Type *</Label>
              <Select value={deviationType} onValueChange={(v) => setDeviationType(v as ConcessionDeviationType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dimensional">Dimensional</SelectItem>
                  <SelectItem value="cosmetic">Cosmetic</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="process">Process</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                  <SelectItem value="packaging">Packaging</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr5551-desc">Description *</Label>
            <Textarea
              id="cr5551-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed description of the deviation"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr5551-reason">Reason for Request *</Label>
            <Textarea
              id="cr5551-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being requested?"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr5551-disposition">Proposed Disposition</Label>
            <Textarea
              id="cr5551-disposition"
              rows={2}
              value={proposedDisposition}
              onChange={(e) => setProposedDisposition(e.target.value)}
              placeholder="Use as-is, rework, sort, etc."
            />
            <p className="text-xs text-muted-foreground">
                What you are asking the customer to accept — use as is, rework,
                repair, or scrap and replace.
              </p>
            </div>

          {/* CR-6510: the identifiers the legacy deviation form carried. QA
              fed back that some were unclear to supplier testers, so each
              carries a one-line explanation rather than a bare label. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cr6510-supplier-ref">Supplier Ref.</Label>
              <Input
                id="cr6510-supplier-ref"
                value={supplierRef}
                onChange={(e) => setSupplierRef(e.target.value)}
                placeholder="Your own reference"
              />
              <p className="text-xs text-muted-foreground">
                Your internal reference for this request, so you can find it again.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cr6510-mm-rev">MM &amp; Rev</Label>
              <Input
                id="cr6510-mm-rev"
                value={mmAndRev}
                onChange={(e) => setMmAndRev(e.target.value)}
                placeholder="e.g. MM-4410 Rev D"
              />
              <p className="text-xs text-muted-foreground">
                Material master / drawing number and revision this deviation is against.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cr6510-serials">Serial No(s)</Label>
              <Input
                id="cr6510-serials"
                value={serialNumbers}
                onChange={(e) => setSerialNumbers(e.target.value)}
                placeholder="e.g. SN-0001, SN-0002"
              />
              <p className="text-xs text-muted-foreground">
                Serial numbers of the affected units, if they are serialised.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cr6510-prev-ref">Previous Deviation Ref.</Label>
              <Input
                id="cr6510-prev-ref"
                value={previousDeviationRef}
                onChange={(e) => setPreviousDeviationRef(e.target.value)}
                placeholder="e.g. QN-2024-0155"
              />
              <p className="text-xs text-muted-foreground">
                An earlier deviation covering the same issue, if there was one.
              </p>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr6510-cust-req">Customer Requirement</Label>
            <Textarea
              id="cr6510-cust-req"
              rows={2}
              value={customerRequirement}
              onChange={(e) => setCustomerRequirement(e.target.value)}
              placeholder="The requirement this departs from"
            />
            <p className="text-xs text-muted-foreground">
              What was originally specified — the Description above says what
              actually happened.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr6510-rcca">Root Cause &amp; Corrective Action</Label>
            <Textarea
              id="cr6510-rcca"
              rows={3}
              value={rootCauseCorrectiveAction}
              onChange={(e) => setRootCauseCorrectiveAction(e.target.value)}
              placeholder="What caused it, and what you have changed"
            />
            <p className="text-xs text-muted-foreground">
              Needed for quality sign-off. Leave blank if the investigation is
              still open.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cr5551-risk">Risk Level *</Label>
              <Select value={riskLevel} onValueChange={(v) => setRiskLevel(v as ConcessionRiskLevel)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cr5551-riskj">Risk Justification</Label>
              <Input
                id="cr5551-riskj"
                value={riskJustification}
                onChange={(e) => setRiskJustification(e.target.value)}
                placeholder="Why this risk level?"
              />
            </div>
          </div>

          {/* CR-6510: attach evidence with the request itself. */}
          <div className="grid gap-2">
            <Label htmlFor="cr6510-files">Attachments</Label>
            {/* Customer review 11 Sep (drag and drop everywhere). */}
            <FileDropZone multiple size="compact" inputId="cr6510-files" onFiles={setFiles} data-testid="concession-files-drop-zone" />
            <p className="text-xs text-muted-foreground">
              A measurement report, a photo of the deviation, or your own
              concession document. Visible to the customer reviewing this.
            </p>
            {!!files.length && (
              <ul className="text-xs text-muted-foreground list-disc pl-4">
                {files.map((file) => (
                  <li key={file.name}>{file.name}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>) : `Submit ${requestType === 'production_permit' ? 'PE Request' : 'Concession'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
